import { useMemo, useState } from 'react';
import { erstelleAbgleich } from './abgleich';
import type {
  AbgleichZeile,
  MitarbeiterFelder,
  MitarbeiterabgleichQuelle,
  MitarbeiterabgleichZusammenfassung,
} from './entities/Mitarbeiterabgleich';
import { MOCK_MITARBEITERABGLEICH_QUELLE } from './mock/mitarbeiterabgleich.mock';
import { ermittleStandorte, uebernehmeMitarbeiter } from './uebernahme';
import { useAbgleichJobs } from './useAbgleichJobs';

function useMitarbeiterabgleichQuelle(): {
  quelle?: MitarbeiterabgleichQuelle;
  error?: string;
} {
  return { quelle: MOCK_MITARBEITERABGLEICH_QUELLE };
}

type MitarbeiterabgleichErgebnis = {
  zeilen: AbgleichZeile[];
  zusammenfassung?: MitarbeiterabgleichZusammenfassung;
  standorte: string[];
  error?: string;
  starteAbgleich: () => void;
  uebernehme: (zeilen: AbgleichZeile[]) => void;
};

export function useMitarbeiterabgleich(): MitarbeiterabgleichErgebnis {
  const { quelle, error } = useMitarbeiterabgleichQuelle();
  const { letzterJob, letzterAbruf, starteAbgleich } = useAbgleichJobs();
  const [uebernommen, setUebernommen] = useState<MitarbeiterFelder[]>();
  const e2nMitarbeiter = uebernommen ?? quelle?.e2nMitarbeiter;

  const abgleich = useMemo(
    () =>
      quelle &&
      e2nMitarbeiter &&
      erstelleAbgleich(e2nMitarbeiter, quelle.datevMitarbeiter),
    [quelle, e2nMitarbeiter],
  );

  const standorte = useMemo(
    () => ermittleStandorte(e2nMitarbeiter ?? []),
    [e2nMitarbeiter],
  );

  const uebernehme = (zeilen: AbgleichZeile[]) => {
    if (!e2nMitarbeiter) {
      return;
    }
    setUebernommen(uebernehmeMitarbeiter(e2nMitarbeiter, zeilen));
  };

  if (!abgleich) {
    return { zeilen: [], standorte, error, starteAbgleich, uebernehme };
  }

  return {
    zeilen: abgleich.zeilen,
    zusammenfassung: {
      gesamt: abgleich.gesamt,
      uebereinstimmend: abgleich.uebereinstimmend,
      letzterJob,
      letzterAbruf,
    },
    standorte,
    starteAbgleich,
    uebernehme,
  };
}
